import { useMemo } from 'react';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createRepository,
  getRepositories,
  getRepository,
  getRepositoryProjects,
} from '~/services/api/repositories';

import type { ProjectWithTasks, Repository } from '~/types';

export const repositoryKeys = {
  all: ['repositories'] as const,
  detail: (id: string) => [...repositoryKeys.all, id] as const,
  list: () => [...repositoryKeys.all, 'list'] as const,
  projects: (id: string) => [...repositoryKeys.all, id, 'projects'] as const,
};

export function useAllRepositoryProjects(repos: Repository[] | undefined): {
  allProjects: ProjectWithTasks[];
  isLoading: boolean;
} {
  const queries = useQueries({
    queries: (repos ?? []).map(repo => ({
      queryKey: repositoryKeys.projects(repo.id),
      queryFn: (): Promise<ProjectWithTasks[]> => getRepositoryProjects(repo.id),
      staleTime: 5 * 60 * 1000,
      enabled: !!repo.id,
    })),
  });

  const isLoading = queries.some(q => q.isLoading);

  const allProjects = useMemo(() => {
    const result: ProjectWithTasks[] = [];
    for (const q of queries) {
      if (q.data) result.push(...q.data);
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- queries array reference changes each render
  }, [queries.filter(q => !q.isLoading).length]);

  return { allProjects, isLoading };
}

export function useCreateRepository(): UseMutationResult<
  Repository,
  Error,
  Parameters<typeof createRepository>[0]
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createRepository,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: repositoryKeys.list() });
    },
  });
}

export function useRepositories(): UseQueryResult<Repository[]> {
  return useQuery({
    queryFn: getRepositories,
    queryKey: repositoryKeys.list(),
  });
}

export function useRepository(id: string): UseQueryResult<Repository> {
  return useQuery({
    enabled: !!id,
    queryFn: () => getRepository(id),
    queryKey: repositoryKeys.detail(id),
  });
}

export function useRepositoryProjects(id: string): UseQueryResult<ProjectWithTasks[]> {
  return useQuery({
    enabled: !!id,
    queryFn: () => getRepositoryProjects(id),
    queryKey: repositoryKeys.projects(id),
  });
}

/**
 * Client-side selector deriving the repositories not yet linked to any
 * product (`productIds` empty or missing) from the single useRepositories()
 * list — never fetched separately, so this is the same source of truth
 * ProductGrid's UnlinkedReposBanner and DiscoverProductsModal both read.
 * Pattern: docs/design-patterns/frontend/anti-patterns/request-fan-out.md
 */
export function useUnlinkedRepositories(): {
  data: Repository[] | undefined;
  isError: boolean;
  isLoading: boolean;
} {
  const { data: repositories, isError, isLoading } = useRepositories();

  const data = useMemo(
    () => repositories?.filter(repository => (repository.productIds?.length ?? 0) === 0),
    [repositories],
  );

  return { data, isError, isLoading };
}
